import { useParams } from '@tanstack/react-router'
import { Pencil } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserByIdQuery } from '@/entities/user'
import { UserAccountLevelSelect } from '@/features/user/user-account-level-select'
import { UserFormDrawer } from '@/features/user/user-form-drawer'
import { UserPasswordActions } from '@/features/user/user-password-actions'
import { UserStatusToggle } from '@/features/user/user-status-toggle'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '?'
}

export function UserDetailPage() {
  const { t } = useTranslation()
  const scopedAdminPath = useScopedAdminPath()
  const { id } = useParams({ strict: false })
  const userId = id ?? ''
  const { data: user, isLoading } = useUserByIdQuery(userId)
  const [editOpen, setEditOpen] = useState(false)

  if (isLoading || !user) {
    return (
      <PageContainer>
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </PageContainer>
    )
  }

  const displayName = user.fullName ?? user.username

  return (
    <PageContainer>
      <PageHeader
        title={displayName}
        subtitle={`@${user.username}`}
        breadcrumbs={[
          { label: t('sidebar.dashboard'), path: scopedAdminPath('/dashboard') },
          { label: t('sidebar.users'), path: scopedAdminPath('/users') },
          { label: displayName },
        ]}
        actions={
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-1.5 h-4 w-4" />
            {t('common.edit')}
          </Button>
        }
      />

      <div className="mb-6 flex items-start gap-4 rounded-lg border border-zinc-100 bg-white p-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={user.avatarUrl ?? undefined} alt={user.username} />
          <AvatarFallback className="text-lg">{initials(displayName)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-zinc-900">{displayName}</h2>
            {user.accountLevel && (
              <Badge variant="secondary">
                {t(`user.accountLevelOptions.${user.accountLevel.toLowerCase().replace('_', '')}`, {
                  defaultValue: user.accountLevel,
                })}
              </Badge>
            )}
            <Badge variant={user.isActive ? 'default' : 'outline'}>
              {user.isActive ? t('common.active') : t('common.inactive')}
            </Badge>
          </div>
          <p className="text-sm text-zinc-500">{user.email ?? '—'}</p>
          <p className="text-xs text-zinc-400">{user.phone ?? '—'}</p>
        </div>
        <UserStatusToggle userId={user.id} isActive={user.isActive} />
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">{t('user.tabs.profile')}</TabsTrigger>
          <TabsTrigger value="security">{t('user.tabs.security')}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            <ProfileRow label={t('user.username')} value={user.username} />
            <ProfileRow label={t('user.fullName')} value={user.fullName ?? '—'} />
            <ProfileRow label={t('user.email')} value={user.email ?? '—'} />
            <ProfileRow label={t('user.phone')} value={user.phone ?? '—'} />
            <ProfileRow label={t('user.tenantId')} value={user.tenantId ?? '—'} />
            <ProfileRow label={t('user.createdAt')} value={user.createdAt} />
          </dl>
        </TabsContent>

        <TabsContent value="security" className="pt-4">
          <div className="grid gap-6 sm:grid-cols-2">
            <UserAccountLevelSelect user={user} />
            <div>
              <p className="mb-2 text-sm font-medium text-zinc-700">{t('user.password')}</p>
              <UserPasswordActions user={user} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <UserFormDrawer open={editOpen} user={user} onClose={() => setEditOpen(false)} />
    </PageContainer>
  )
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-zinc-400">{label}</dt>
      <dd className="text-sm text-zinc-800">{value}</dd>
    </div>
  )
}

export default UserDetailPage
